import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImageUrl, buildSrcSet } from "@/components/ImageUpload";

export function SearchBar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get(`/search?q=${encodeURIComponent(term)}`)
        .then((r) => {
          setResults(r.data);
          setOpen(true);
        })
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goFull = () => {
    const term = q.trim();
    if (term.length < 2) return;
    setOpen(false);
    navigate(`/buscar?q=${encodeURIComponent(term)}`);
  };

  const total = results
    ? results.articles.length + results.songs.length + results.events.length
    : 0;

  return (
    <div ref={boxRef} className="relative w-full max-w-2xl" data-testid="home-search">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          goFull();
        }}
        className="flex items-center gap-3 rounded-full bg-white border border-black/15 px-5 py-3 shadow-sm focus-within:border-[#2A00D9] transition-colors"
      >
        <Search size={20} className="text-[#2A00D9] flex-shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Busca artistas, lanzamientos, entrevistas, canciones…"
          className="flex-1 bg-transparent outline-none text-base text-[#1A1A1A] placeholder:text-[#9A9A9A]"
          data-testid="home-search-input"
          aria-label="Buscar"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setResults(null);
              setOpen(false);
            }}
            className="text-[#4A4A4A] hover:text-[#1A1A1A] flex-shrink-0"
            data-testid="home-search-clear"
            aria-label="Limpiar"
          >
            <X size={18} />
          </button>
        )}
      </form>

      {open && results && (
        <div
          className="absolute z-30 mt-2 w-full rounded-2xl bg-white border border-black/10 shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto"
          data-testid="home-search-results"
        >
          {total === 0 ? (
            <p className="px-5 py-6 text-sm text-[#4A4A4A]" data-testid="home-search-empty">
              {loading ? "Buscando…" : `Sin resultados para “${q.trim()}”.`}
            </p>
          ) : (
            <div className="py-2">
              {results.articles.length > 0 && (
                <Group label="Noticias">
                  {results.articles.slice(0, 5).map((a, i) => (
                    <ResultLink
                      key={`a-${a.slug}`}
                      to={`/noticias/${a.slug}`}
                      onClick={() => setOpen(false)}
                      image={a.cover_image}
                      title={a.title}
                      sub={`${a.format || "Noticia"} · ${a.author}`}
                      testid={`search-result-article-${i}`}
                    />
                  ))}
                </Group>
              )}
              {results.songs.length > 0 && (
                <Group label="Significado de canciones">
                  {results.songs.slice(0, 5).map((s, i) => (
                    <ResultLink
                      key={`s-${s.slug}`}
                      to={`/canciones/${s.slug}`}
                      onClick={() => setOpen(false)}
                      image={s.cover_image}
                      title={s.song}
                      sub={s.artist}
                      testid={`search-result-song-${i}`}
                    />
                  ))}
                </Group>
              )}
              {results.events.length > 0 && (
                <Group label="Agenda">
                  {results.events.slice(0, 5).map((e, i) => (
                    <ResultLink
                      key={`e-${i}`}
                      to="/agenda"
                      onClick={() => setOpen(false)}
                      image={e.image}
                      title={e.title}
                      sub={`${e.venue}${e.city ? ", " + e.city : ""}`}
                      testid={`search-result-event-${i}`}
                    />
                  ))}
                </Group>
              )}
              <button
                onClick={goFull}
                className="w-full text-left px-5 py-3 text-sm font-semibold text-[#2A00D9] hover:bg-[#2A00D9]/5 inline-flex items-center gap-2 border-t border-black/5"
                data-testid="home-search-see-all"
              >
                Ver todos los resultados ({total}) <ArrowRight size={15} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <p className="px-5 pt-3 pb-1 eyebrow text-[10px]">{label}</p>
      {children}
    </div>
  );
}

function ResultLink({ to, onClick, image, title, sub, testid }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-5 py-2.5 hover:bg-black/5 transition-colors"
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-[#F3F2EE]">
        {image && <img src={resolveImageUrl(image)} srcSet={buildSrcSet(image)} sizes="48px" alt={title} width={48} height={48} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{title}</p>
        <p className="text-xs text-[#4A4A4A] truncate">{sub}</p>
      </div>
    </Link>
  );
}
